import type { AgentTool } from '@saaga/shared/interfaces/agent-tool.interface'
import { useMemo } from 'react'
import type { ControllerRenderProps } from 'react-hook-form'

import type { AgentNodeFormSchemaType } from '../agency-node-form.schema'

interface ToolsHandlers {
  selectedToolList: AgentTool[]
  handleToggle: (toolId: string) => void
  handleSelectAll: (filteredIdList: string[]) => void
  handleDeselectAll: (filteredIdList: string[]) => void
  handleRemove: (toolId: string) => void
}

const getSelectedToolList = (toolList: AgentTool[], selectedIdList: string[]): AgentTool[] =>
  selectedIdList
    .map((id) => toolList.find((tool) => tool.id === id))
    .filter((tool): tool is AgentTool => tool !== undefined)

export const useToolsHandlers = (
  field: ControllerRenderProps<AgentNodeFormSchemaType, 'toolList'>,
  toolList: AgentTool[]
): ToolsHandlers => {
  const selectedToolList = useMemo(
    () => getSelectedToolList(toolList, field.value),
    [toolList, field.value]
  )

  const handleToggle = (toolId: string): void => {
    if (field.value.includes(toolId)) {
      field.onChange(field.value.filter((id) => id !== toolId))
      return
    }
    field.onChange([...field.value, toolId])
  }

  const handleSelectAll = (filteredIdList: string[]): void => {
    const newSelection = [...new Set([...field.value, ...filteredIdList])]
    field.onChange(newSelection)
  }

  const handleDeselectAll = (filteredIdList: string[]): void => {
    const filteredIdSet = new Set(filteredIdList)
    field.onChange(field.value.filter((id) => !filteredIdSet.has(id)))
  }

  const handleRemove = (toolId: string): void => {
    field.onChange(field.value.filter((id) => id !== toolId))
  }

  return { selectedToolList, handleToggle, handleSelectAll, handleDeselectAll, handleRemove }
}
